import { Outlet, NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Ticket, Users as UsersIcon, Building2,
  Shield, Tag, RefreshCw, FileText, Settings, Scale, MonitorSmartphone, Menu, X, Activity,
  PanelLeftClose, PanelLeftOpen, Lightbulb, UserCheck,
} from 'lucide-react';
import { useAuthStore } from '../../stores/auth-store';
import { useThemeStore } from '../../stores/theme-store';
import { NotificationDropdown } from '../ui/NotificationDropdown';
import { ThemeToggle } from '../ui/ThemeToggle';
import { cn } from '../../lib/utils';
import { useState, useEffect } from 'react';
import { useActivityHeartbeat } from '../../hooks/use-activity';
import { useLogoutAction } from '../../hooks/use-auth';

const mainNav: Array<{
  to: string;
  icon: typeof Ticket;
  label: string;
  end?: boolean;
  isActive?: (pathname: string, search: string) => boolean;
}> = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/admin/tickets', icon: Ticket, label: 'Tichete', isActive: (pathname, search) => pathname === '/admin/tickets' && !search.includes('mine=1') },
  // Preset de filtrare: tichetele alocate utilizatorului curent, în lucru/estimare
  { to: '/admin/tickets?mine=1', icon: UserCheck, label: 'Tichetele mele', isActive: (pathname, search) => pathname === '/admin/tickets' && search.includes('mine=1') },
  { to: '/admin/feature-requests', icon: Lightbulb, label: 'Sugestii' },
  { to: '/admin/clients', icon: Building2, label: 'Clienți' },
  // Vizibil și pentru agenți — pagina afișează doar profilul și preferințele
  // de notificare; secțiunea „Setări globale” rămâne ADMIN-only (SettingsPage).
  { to: '/admin/settings', icon: Settings, label: 'Setări' },
];

const adminNav = [
  { to: '/admin/users', icon: UsersIcon, label: 'Utilizatori', adminOnly: true },
  { to: '/admin/sla', icon: Scale, label: 'SLA', adminOnly: true },
  { to: '/admin/tags', icon: Tag, label: 'Etichete', adminOnly: true },
  { to: '/admin/webcon', icon: RefreshCw, label: 'WebCon', adminOnly: true },
  { to: '/admin/microsoft', icon: MonitorSmartphone, label: 'Microsoft', adminOnly: true },
  { to: '/admin/audit', icon: FileText, label: 'Audit', adminOnly: true },
  { to: '/admin/activity', icon: Activity, label: 'Activitate', adminOnly: true },
];

export function AdminLayout() {
  const user = useAuthStore((s) => s.user);
  const logout = useLogoutAction();
  const dark = useThemeStore((s) => s.dark);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('helpdesk_sidebar_collapsed') === 'true'; } catch { return false; }
  });
  const location = useLocation();
  const navigate = useNavigate();

  const isAdmin = user?.role === 'ADMIN';
  const isChatRoute = location.pathname.match(/^\/admin\/feature-requests\/[^/]+\/chat$/);
  useActivityHeartbeat();

  // Close mobile menu on route change
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem('helpdesk_sidebar_collapsed', String(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const sidebarContent = (collapsed: boolean) => (
    <>
      <div
        className={cn(
          'border-b border-[color:var(--surface-line)] py-4',
          collapsed ? 'flex min-h-20 items-center justify-center px-2' : 'relative min-h-[8.75rem] px-5',
        )}
      >
        <Link to="/admin" title="Dashboard" className="block">
          {collapsed ? (
            <img
              alt="Encorsa"
              src={dark ? '/logo_encorsa_ALB_bulina.png' : '/logo_encorsa_COLOR_bulina.png'}
              className="h-10 w-10 transition-all duration-200"
            />
          ) : (
            <div className="min-w-0 pr-10">
              <p className="app-kicker">Encorsa support</p>
              <p className="mt-1 text-sm font-semibold text-[color:var(--ink-strong)]">Consola operationala</p>
              <img
                alt="Logo"
                src={dark ? '/logo_encorsa_ALB_mic.png' : '/logo_encorsa_COLOR_mic.png'}
                className="mt-4 h-8 w-auto transition-all duration-200"
              />
            </div>
          )}
        </Link>
        <button className="absolute right-4 top-4 md:hidden" onClick={() => setMobileMenuOpen(false)}><X size={20} className="text-[color:var(--ink-muted)]" /></button>
      </div>

      <nav className="flex-1 space-y-1.5 overflow-y-auto p-3.5">
        {mainNav.map((item) => {
          // Items with a custom isActive (e.g. "Tichetele mele" — same path, different query)
          // use a plain Link with manual active detection, since NavLink matches by path only.
          if (item.isActive) {
            const active = item.isActive(location.pathname, location.search);
            return (
              <Link
                key={item.to}
                to={item.to}
                title={collapsed ? item.label : undefined}
                className={cn(
                  'flex items-center gap-3 rounded-[1.15rem] border px-3.5 py-3 text-sm font-medium transition-all duration-200',
                  collapsed && 'justify-center px-0',
                  active
                    ? 'border-[color:var(--surface-line)] bg-[var(--surface-panel-strong)] text-primary shadow-[var(--shadow-soft)]'
                    : 'border-transparent text-[color:var(--ink-muted)] hover:border-[color:var(--surface-line)] hover:bg-[var(--surface-panel-strong)] hover:text-[color:var(--ink-strong)]',
                )}
              >
                <item.icon size={20} />
                {!collapsed && item.label}
              </Link>
            );
          }
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-[1.15rem] border px-3.5 py-3 text-sm font-medium transition-all duration-200',
                  collapsed && 'justify-center px-0',
                  isActive
                    ? 'border-[color:var(--surface-line)] bg-[var(--surface-panel-strong)] text-primary shadow-[var(--shadow-soft)]'
                    : 'border-transparent text-[color:var(--ink-muted)] hover:border-[color:var(--surface-line)] hover:bg-[var(--surface-panel-strong)] hover:text-[color:var(--ink-strong)]',
                )
              }
            >
              <item.icon size={20} />
              {!collapsed && item.label}
            </NavLink>
          );
        })}

        {isAdmin && (
          <>
            <div className="my-3 border-t border-[color:var(--surface-line)]" />
            {!collapsed && (
              <p className="app-kicker px-3">
                Administrare
              </p>
            )}
            {adminNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                title={collapsed ? item.label : undefined}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-[1.15rem] border px-3.5 py-3 text-sm font-medium transition-all duration-200',
                    collapsed && 'justify-center px-0',
                    isActive
                      ? 'border-[color:var(--surface-line)] bg-[var(--surface-panel-strong)] text-primary shadow-[var(--shadow-soft)]'
                      : 'border-transparent text-[color:var(--ink-muted)] hover:border-[color:var(--surface-line)] hover:bg-[var(--surface-panel-strong)] hover:text-[color:var(--ink-strong)]',
                  )
                }
              >
                <item.icon size={20} />
                {!collapsed && item.label}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      <div className="hidden border-t border-[color:var(--surface-line)] p-3 md:flex">
        <button
          onClick={toggleSidebar}
          className={cn(
            'flex w-full items-center gap-2 rounded-[1.15rem] border border-transparent px-3 py-2.5 text-sm font-medium text-[color:var(--ink-muted)] transition-all duration-200 hover:border-[color:var(--surface-line)] hover:bg-[var(--surface-panel-strong)] hover:text-[color:var(--ink-strong)]',
            collapsed && 'justify-center px-0',
          )}
          title={collapsed ? 'Extinde meniul' : 'Restrânge meniul'}
        >
          {collapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
          {!collapsed && 'Restrânge'}
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Desktop sidebar */}
      <aside className={cn(
        'hidden shrink-0 flex-col border-r border-[color:var(--surface-line)] bg-[var(--surface-panel)] transition-all duration-300 md:flex',
        sidebarCollapsed ? 'w-[68px]' : 'w-[260px]',
      )}>
        {sidebarContent(sidebarCollapsed)}
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileMenuOpen(false)} />
          <aside className="relative z-50 flex h-full w-[260px] flex-col bg-[var(--surface-panel)]">
            {sidebarContent(false)}
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-16 shrink-0 items-center gap-4 border-b border-[color:var(--surface-line)] bg-[var(--surface-panel)] px-4 md:px-6">
          <button className="md:hidden" onClick={() => setMobileMenuOpen(true)}>
            <Menu size={22} className="text-[color:var(--ink-muted)]" />
          </button>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => navigate('/admin/feature-requests/new')}
            title="Raportează o sugestie, o funcționalitate nouă sau un bug"
            className="inline-flex h-9 items-center gap-1.5 rounded-full border border-[color:var(--surface-line)] bg-[var(--surface-panel-strong)] px-3.5 text-xs font-semibold text-[color:var(--ink-base)] transition-all duration-200 hover:-translate-y-px hover:border-[color:var(--surface-line-strong)] hover:text-[color:var(--ink-strong)]"
          >
            <Lightbulb size={15} className="text-accent-dark" />
            <span className="hidden sm:inline">Sugestie</span>
          </button>
          <ThemeToggle />
          <NotificationDropdown />
          <div className="flex items-center gap-2">
            <Shield size={16} className="text-primary" />
            <span className="hidden text-sm font-medium text-[color:var(--ink-strong)] sm:inline">{user?.name}</span>
            <span className="hidden sm:inline rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold text-primary">
              {user?.role}
            </span>
            <button onClick={logout} className="text-xs text-[color:var(--ink-soft)] hover:text-[color:var(--ink-strong)]">
              Ieșire
            </button>
          </div>
        </header>

        <main
          className={
            isChatRoute
              ? 'flex flex-1 flex-col overflow-hidden bg-transparent'
              : 'flex flex-1 flex-col overflow-y-auto overflow-x-hidden bg-transparent p-4 md:p-6 lg:p-8'
          }
        >
          {isChatRoute ? (
            <Outlet />
          ) : (
            <div className="mx-auto w-full max-w-[1560px]">
              <Outlet />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
